import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, TagIcon, ClockIcon } from '@heroicons/react/24/outline';
import apiService from '../services/apiService';
import { getImageUrlWithEnv } from '../utils/imageUrl';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        const articleData = await apiService.getNewsById(id);
        if (articleData) {
          setArticle(articleData);
        } else {
          setError('სტატია ვერ მოიძებნა');
        }
      } catch (err) {
        console.error('Error loading article:', err);
        setError('სტატიის ჩატვირთვა ვერ მოხერხდა');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadArticle();
    }
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ka-GE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('ka-GE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 font-georgian mb-2">
            {error || 'სტატია ვერ მოიძებნა'}
          </h1>
          <p className="text-gray-600 font-georgian mb-6">
            შესაძლოა სტატია წაიშალა ან არ არსებობს
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 font-georgian"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            მთავარ გვერდზე დაბრუნება
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-6">
            <button
              onClick={() => navigate('/')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 font-georgian">სტატია</h1>
              <p className="mt-1 text-sm text-gray-500 font-georgian">FC ივერია - ოფიციალური საიტი</p>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Article Image */}
          {article.image && (
            <div className="aspect-w-16 aspect-h-9">
              <img
                src={getImageUrlWithEnv(article.image)}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <div className="p-6 md:p-8">
            {/* Article Meta */}
            <div className="flex flex-wrap items-center text-sm text-gray-500 mb-6">
              <div className="flex items-center mr-6 mb-2">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span className="font-georgian">{formatDate(article.date)}</span>
              </div>
              <div className="flex items-center mr-6 mb-2">
                <ClockIcon className="h-4 w-4 mr-1" />
                <span className="font-georgian">{formatTime(article.createdAt)}</span>
              </div>
              {article.category && (
                <div className="flex items-center mb-2">
                  <TagIcon className="h-4 w-4 mr-1" />
                  <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs font-georgian">
                    {article.category}
                  </span>
                </div>
              )}
            </div>

            {/* Article Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-georgian leading-tight">
              {article.title}
            </h1>

            {/* Article Excerpt */}
            {article.excerpt && (
              <div className="text-xl text-gray-600 mb-8 font-georgian leading-relaxed">
                {article.excerpt}
              </div>
            )}

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-800 leading-relaxed font-georgian whitespace-pre-wrap">
                {article.content || article.excerpt}
              </div>
            </div>

            {/* Article Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500 font-georgian">
                  გამოქვეყნდა: {formatDate(article.createdAt)}
                  {article.updatedAt !== article.createdAt && (
                    <span className="ml-2">
                      (განახლდა: {formatDate(article.updatedAt)})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 font-georgian"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  უკან
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default NewsDetail;
